import { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { setSentryUser } from '@/lib/sentry';

const AuthContext = createContext(null);
const RESERVED_SEGMENTS = ['auth', 'app', 'admin', 'api', 'staff', 'staff-portal', 'finance', 'referral-staff', 'agent'];
const PLATFORM_DOMAIN = (process.env.REACT_APP_PLATFORM_DOMAIN || 'vegaslobby.vip').toLowerCase();

function resolveTenantSlug() {
  const path = window.location.pathname;
  const match = path.match(/^\/([a-z0-9-]+)\//i);
  if (match && !RESERVED_SEGMENTS.includes(match[1])) return match[1];

  const host = (window.location.hostname || '').toLowerCase().replace(/^www\./, '');
  const suffix = `.${PLATFORM_DOMAIN}`;
  if (host.endsWith(suffix)) {
    const slug = host.slice(0, -suffix.length);
    if (slug && !RESERVED_SEGMENTS.includes(slug)) return slug;
  }
  return null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantSlug, setTenantSlug] = useState(() => resolveTenantSlug());

  useEffect(() => {
    setTenantSlug(resolveTenantSlug());
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (!token) { setUser(null); setSentryUser(null); setLoading(false); return; }
      const res = await api.get('/api/auth/me');
      setUser(res.data);
      setSentryUser(res.data);
    } catch {
      localStorage.removeItem('session_token');
      setUser(null);
      setSentryUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (identifier, password, tenant = null) => {
    const res = await api.post('/api/auth/login', {
      identifier,
      password,
      tenant_slug: tenant || tenantSlug
    });
    const token = res.data.token || res.data.session_id;
    if (token) localStorage.setItem('session_token', token);
    // Reset player's last-viewed tab so a fresh login always lands on the
    // main lobby (home), not the tab they happened to be on before logout.
    try { localStorage.removeItem('player_active_tab'); } catch {}
    setUser(res.data.user);
    // Iter 142.6 — pin the player to all subsequent Sentry events
    setSentryUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, fullName, username = null, referralCode = null, tenant = null, referralSource = null) => {
    const res = await api.post('/api/auth/signup', {
      email,
      password,
      username: username || undefined,
      referral_code: referralCode || undefined,
      referral_source: referralSource || undefined,
      tenant_slug: tenant || tenantSlug || undefined,
    });
    const token = res.data.token || res.data.session_id;
    if (token) localStorage.setItem('session_token', token);
    try { localStorage.removeItem('player_active_tab'); } catch {}
    setUser(res.data.user);
    setSentryUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    try { await api.post('/api/auth/logout'); } catch {}
    localStorage.removeItem('session_token');
    // Drop the tab memory on logout too, so if another user logs in from
    // the same browser they get the clean landing page.
    try { localStorage.removeItem('player_active_tab'); } catch {}
    setUser(null);
    // Iter 142.6 — clear Sentry user so post-logout errors aren't
    // attributed to the old player.
    setSentryUser(null);
  };

  // Iter 142.8: hydrate the AuthContext from outside (used by the
  // Google OAuth callback page after it exchanges the code for a session).
  // Without this, the next route render would do an unnecessary /me round
  // trip just to learn what we already know.
  const setUserDirect = (u) => setUser(u);

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout, setUserDirect,
      checkAuth, tenantSlug, setTenantSlug
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
